import type { ContentBlock } from '@agentclientprotocol/sdk/experimental/v2'
import { api, artifactFileUrl } from '@web/api/client'
import type {
  AgentProviderId,
  AppDefinition,
  BridgeConfig,
  ProviderDefinition,
  SessionArtifact,
  SessionConfig,
  SessionRecord,
} from '@web/api/types'
import {
  MAX_MESSAGE_ATTACHMENT_BYTES,
  MAX_UPLOAD_BYTES,
  uploadSessionFile,
} from '@web/api/uploads'
import { Row, RowGroup, Sheet } from '@web/components/mobile/Sheet'
import { CircleButton } from '@web/components/mobile/TopBar'
import { PendingSessionView } from '@web/components/PendingSessionView'
import {
  initialLaunchSettings,
  LaunchPills,
  launchControls,
  launchSelection,
  launchSettings,
  launchSummary,
  repositorySupport,
  usesWorkspace,
} from '@web/components/ProviderSettings'
import {
  RepositoryChip,
  RepositoryDialog,
  repositoryCheckout,
  type SelectedRepository,
  selectedFromCheckouts,
} from '@web/components/RepositoryPicker'
import { SendButton } from '@web/components/SendButton'
import { ActionMenu } from '@web/components/ui/ActionMenu'
import { Icon } from '@web/components/ui/icons'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { handleComposerEnter } from '@web/state/enter-key-behavior'
import { useIdentity } from '@web/state/identity'
import { useEnterKeyBehavior } from '@web/state/local-enter-key-behavior'
import { getSessionStore } from '@web/state/session-store'
import {
  adoptLaunch,
  beginLaunch,
  markLaunch,
} from '@web/state/startup-telemetry'
import {
  type ClipboardEvent,
  type DragEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

type Props = {
  app?: AppDefinition
  config: BridgeConfig
  preset?: Partial<SessionConfig>
  onClose: () => void
  onCreated: (record: SessionRecord) => void
  presentation?: 'dialog' | 'pane'
  pendingHost?: RefObject<HTMLElement | null>
  /** Phone layout for the pane: the greeting owns the screen and the composer docks at the bottom. */
  narrow?: boolean
  provider?: AgentProviderId | null
}
type InitialAttachment = { id: string; name: string; file: File }
type UploadedInitialAttachment = {
  attachment: InitialAttachment
  data: string
  upload: SessionArtifact
}
export function NewSessionDialog({
  app,
  config,
  preset,
  onClose,
  onCreated,
  pendingHost,
  presentation = 'dialog',
  narrow = false,
  provider,
}: Props) {
  const identity = useIdentity()
  const behavior = useEnterKeyBehavior(
    identity.state?.current_user?.enter_key_behavior,
  )
  const factory = preset?.app_id === 'loop-engineering'
  const supportedProviders = (providers: ProviderDefinition[] = []) =>
    providers
      .filter((p) => p.id === 'claude' || p.id === 'codex')
      .map((p) => ({
        ...p,
        execution_modes: p.execution_modes.filter((mode) =>
          usesWorkspace(mode.id),
        ),
      }))
  const launchConfigCatalog = factory
    ? {
        ...config,
        providers: supportedProviders(config.providers),
        environments: config.environments.map((environment) => ({
          ...environment,
          health: {
            ...environment.health,
            providers: supportedProviders(environment.health.providers),
          },
        })),
      }
    : config
  const [settings, setSettings] = useState<Partial<SessionConfig>>(() =>
    initialLaunchSettings(launchConfigCatalog, {
      ...config.defaults,
      ...preset,
      ...(provider ? { provider } : {}),
      acp_auth_method: null,
    }),
  )
  const selection = launchSelection(launchConfigCatalog, settings)
  const agentName =
    selection.provider?.title ??
    (provider === 'codex'
      ? 'Codex'
      : provider === 'opencode'
        ? 'OpenCode'
        : provider === 'puck'
          ? 'Puck'
          : 'This agent')
  const [prompt, setPrompt] = useState('')
  const [showSession, setShowSession] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('Preparing session…')
  const [followup, setFollowup] = useState('')
  const followupDraft = useRef('')
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const [attachments, setAttachments] = useState<InitialAttachment[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const createdSession = useRef<SessionRecord | null>(null)
  const deferredInitialPrompt = useRef(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // One repository, cloned on its default branch; a fork keeps its source's.
  const [repository, setRepository] = useState<SelectedRepository | null>(() =>
    selectedFromCheckouts(settings.repositories),
  )
  const [repositoryDialog, setRepositoryDialog] = useState(false)
  const [options, setOptions] = useState(false)
  const updateSettings = (patch: Partial<SessionConfig>) =>
    setSettings((current) => {
      const next = { ...current, ...patch }
      // A repository needs a workspace; switching to an execution without
      // one drops the chip rather than sending a launch the API rejects.
      if (
        !repositorySupport(launchSelection(launchConfigCatalog, next)).available
      )
        setRepository(null)
      return next
    })
  const dialog = useRef<HTMLDivElement>(null)
  const promptInput = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  useDialogFocus(dialog, onClose, !busy, undefined, presentation === 'dialog')
  useEffect(() => {
    promptInput.current?.focus()
  }, [])

  const addFiles = async (files: Iterable<File>) => {
    if (busy) return
    const next: InitialAttachment[] = []
    let failure: string | null = null
    let attachedBytes = attachments.reduce(
      (total, attachment) => total + attachment.file.size,
      0,
    )
    for (const file of files) {
      if (attachments.length + next.length >= 16) {
        failure = 'An initial message can include up to 16 files.'
        break
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        failure = `${file.name} is larger than 4 MB`
        continue
      }
      if (attachedBytes + file.size > MAX_MESSAGE_ATTACHMENT_BYTES) {
        failure =
          'An initial message can include up to 16 files and 8 MB of attachments.'
        continue
      }
      next.push({
        id: crypto.randomUUID(),
        name: file.name || 'attached file',
        file,
      })
      attachedBytes += file.size
    }
    if (failure) {
      setAttachmentError(`No files were added from this selection: ${failure}`)
      return
    }
    setAttachmentError(null)
    if (next.length) setAttachments((current) => [...current, ...next])
  }
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = [...event.clipboardData.files]
    if (!files.length) return
    event.preventDefault()
    void addFiles(files)
  }
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.files.length)
      void addFiles([...event.dataTransfer.files])
  }
  const submit = async () => {
    if (!selection.available || busy || attachmentError) return
    setBusy(true)
    setShowSession(true)
    setConnectionStatus('Preparing session…')
    setError('')
    beginLaunch('new-session-dialog')
    try {
      const initialPrompt = prompt.trim()
      const launchConfig = launchSettings({
        ...settings,
        repositories: repository ? [repositoryCheckout(repository)] : [],
        protocol: 'acp',
        acp_version: 2,
        provider: selection.provider!.id,
        execution_mode: selection.mode!.id,
        environment: selection.environment,
        // Workspace-backed sessions start in the environment's workspace.
        // Directory selection is available after the session starts.
        project: usesWorkspace(selection.mode?.id)
          ? (settings.project ?? '')
          : '',
        title: settings.title?.trim() || null,
      })
      const launchPrompt = attachments.length
        ? undefined
        : initialPrompt || undefined
      const record =
        createdSession.current ??
        (settings.app_id === 'loop-engineering'
          ? (
              await api.launchApp('loop-engineering', {
                packageDigest: settings.package_digest || undefined,
                config: launchConfig,
                prompt: launchPrompt,
              })
            ).session
          : app?.descriptor?.schemaVersion === 2
            ? (
                await api.launchApp(app.id, {
                  packageDigest: settings.package_digest || app.digest,
                  config: launchConfig,
                  prompt: launchPrompt,
                })
              ).session
            : await api.createSession(launchConfig, launchPrompt))
      if (!record) throw new Error('The agent could not create a session')
      const store = getSessionStore(record.id, record)
      if (launchPrompt)
        store.seedTitleMessage(`initial:${record.id}`, launchPrompt)
      if (launchPrompt)
        void api
          .generateSessionTitle(record.id, `initial:${record.id}`, [
            launchPrompt,
          ])
          .catch(() => undefined)
      if (!createdSession.current)
        deferredInitialPrompt.current = attachments.length > 0
      createdSession.current = record
      if (attachments.length) setConnectionStatus('Uploading attached files…')
      const uploaded: UploadedInitialAttachment[] = []
      for (const attachment of attachments) {
        const { upload, data } = await uploadSessionFile(
          record.id,
          attachment.file,
        )
        uploaded.push({ attachment, data, upload })
      }
      if (deferredInitialPrompt.current && (initialPrompt || uploaded.length))
        store.queueStartupMessage({
          type: 'queue',
          content: await initialMessageContent(initialPrompt, uploaded),
        })
      store.saveComposerDraft({
        ...store.getComposerDraft(),
        text: followupDraft.current,
      })
      markLaunch('create_response')
      adoptLaunch(record.id)
      if (active.current) {
        onCreated(record)
        requestAnimationFrame(() =>
          window.dispatchEvent(
            new CustomEvent('bridge:focus-composer', {
              detail: { sessionId: record.id },
            }),
          ),
        )
      }
    } catch (failure) {
      setError(
        `${createdSession.current ? 'Session created. Retry to finish uploading files: ' : ''}${(failure as Error).message}`,
      )
      setBusy(false)
    }
  }
  const dialogPresentation = presentation === 'dialog'
  if (showSession) {
    const pending = (
      <PendingSessionView
        app={app}
        root={dialog}
        modal={dialogPresentation}
        label={settings.fork_session ? 'Fork session' : 'New session'}
        title={
          settings.title?.trim() ||
          prompt.trim().split('\n')[0]?.slice(0, 100) ||
          'New session'
        }
        agentName={agentName}
        model={settings.model}
        narrow={narrow}
        prompt={prompt.trim()}
        filenames={attachments.map((file) => file.name)}
        draft={followup}
        onDraftChange={(value) => {
          followupDraft.current = value
          setFollowup(value)
        }}
        status={connectionStatus}
        error={error}
        onRetry={() => void submit()}
        onEdit={() => {
          setShowSession(false)
          setError('')
        }}
      />
    )
    return pendingHost?.current
      ? createPortal(pending, pendingHost.current)
      : pending
  }
  const support = repositorySupport(selection)
  const addRepository = {
    id: 'repository',
    label: repository ? 'Change repository' : 'Add repository',
    description: support.reason,
    icon: 'repo' as const,
    disabled: busy || !support.available,
    onSelect: () => setRepositoryDialog(true),
  }
  const repositoryDialogElement = (
    <RepositoryDialog
      open={repositoryDialog}
      host={dialog}
      selected={repository}
      onClose={() => setRepositoryDialog(false)}
      onSelect={setRepository}
    />
  )
  const docked = narrow && !dialogPresentation
  const submitButton = (
    <SendButton
      className={`btn btn-primary h-9 w-9 justify-center rounded-full px-0 ${docked ? 'm-send' : ''}`}
      onSend={() => void submit()}
      disabled={busy || !!attachmentError || !selection.available}
      label={settings.fork_session ? 'Fork session' : 'Create session'}
    >
      {busy ? (
        <Icon name="loader" size={15} className="spin" />
      ) : (
        <Icon name="send" size={16} />
      )}
    </SendButton>
  )
  const composerChips = (repository || attachments.length > 0) && (
    <div className="composer-chips">
      {repository && (
        <RepositoryChip
          repository={repository}
          disabled={busy}
          onRemove={() => setRepository(null)}
        />
      )}
      {attachments.map((file) => (
        <span
          key={file.id}
          className="flex items-center gap-1 rounded-control border border-line bg-inset px-2 py-1 text-xs"
        >
          <Icon name="fileText" size={13} className="text-muted" />
          <span className="max-w-40 truncate">{file.name}</span>
          <button
            className="text-faint hover:text-fg"
            disabled={busy}
            aria-label={`Remove ${file.name}`}
            onClick={() => {
              setAttachmentError(null)
              setAttachments((items) =>
                items.filter((item) => item.id !== file.id),
              )
            }}
          >
            <Icon name="close" size={12} />
          </button>
        </span>
      ))}
    </div>
  )
  const attachmentErrorMessage = attachmentError && (
    <p role="alert" className="px-4 pt-2 text-xs text-danger">
      {attachmentError}{' '}
      <button
        type="button"
        className="underline"
        onClick={() => setAttachmentError(null)}
      >
        Discard rejected selection
      </button>
    </p>
  )
  const promptField = (
    <textarea
      className={
        docked
          ? 'w-full resize-none bg-transparent outline-none placeholder:text-faint'
          : 'min-h-32 w-full resize-none bg-transparent px-4 py-3 text-[15px] outline-none placeholder:text-faint'
      }
      rows={docked ? 2 : 4}
      ref={promptInput}
      aria-label="First message"
      placeholder="Ask the agent to build, fix, or explore something…"
      disabled={busy}
      value={prompt}
      onChange={(event) => setPrompt(event.target.value)}
      onPaste={onPaste}
      onKeyDown={(event) =>
        handleComposerEnter(event, behavior, setPrompt, () => void submit())
      }
    />
  )
  const fileField = (
    <input
      ref={fileInput}
      type="file"
      multiple
      hidden
      disabled={busy}
      onChange={(event) => {
        if (event.target.files) void addFiles([...event.target.files])
        event.target.value = ''
      }}
    />
  )
  if (docked) {
    const summary = launchSummary(
      launchControls({
        config: launchConfigCatalog,
        settings,
        disabled: busy,
        providerLocked: !!settings.fork_session,
        onChange: () => {},
      }),
    )
    const pills = (variant: 'compact' | 'rows') => (
      <LaunchPills
        variant={variant}
        config={launchConfigCatalog}
        settings={settings}
        disabled={busy}
        providerLocked={!!settings.fork_session}
        onChange={updateSettings}
      />
    )
    return (
      <div
        ref={dialog}
        role="region"
        aria-label={settings.fork_session ? 'Fork session' : 'New session'}
        tabIndex={-1}
        className="m-launcher new-session-launcher"
        onDragOver={(event) => event.preventDefault()}
        onDrop={onDrop}
      >
        <>
          <div className="m-launcher-hero">
            <h1>
              {settings.fork_session
                ? 'Continue this work in a new session'
                : 'What should we work on?'}
            </h1>
            {summary && <p className="m-launcher-summary">{summary}</p>}
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            {!selection.available && (
              <p role="status" className="text-sm text-muted">
                {provider === 'codex' ? 'Codex' : 'This agent'} is unavailable
                in this environment. Choose another execution option from the
                menu or try again when the runtime is ready.
              </p>
            )}
          </div>
          <div className="m-launcher-dock">
            <div className="new-session-composer card relative">
              {composerChips}
              {attachmentErrorMessage}
              {promptField}
              <div className="new-session-actions">
                <CircleButton
                  icon="plus"
                  label="Options"
                  expanded={options}
                  disabled={busy}
                  onClick={() => setOptions(true)}
                />
                {fileField}
                {pills('compact')}
                <span className="flex-1" />
                {submitButton}
              </div>
            </div>
          </div>
        </>
        <Sheet
          open={options}
          onClose={() => setOptions(false)}
          title="Options"
          eyebrow={settings.fork_session ? 'Fork session' : 'New session'}
        >
          {pills('rows')}
          <RowGroup>
            <Row
              icon="plus"
              label="Attach files"
              description="Included in the first message."
              disabled={busy}
              onClick={() => {
                setOptions(false)
                fileInput.current?.click()
              }}
            />
            {support.supported && (
              <Row
                icon="repo"
                label={addRepository.label}
                description={addRepository.description}
                disabled={addRepository.disabled}
                onClick={() => {
                  setOptions(false)
                  setRepositoryDialog(true)
                }}
              />
            )}
          </RowGroup>
        </Sheet>
        {repositoryDialogElement}
      </div>
    )
  }
  return (
    <div
      className={
        dialogPresentation
          ? 'fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-4'
          : 'new-session-pane'
      }
      onMouseDown={(event) => {
        if (dialogPresentation && event.target === event.currentTarget && !busy)
          onClose()
      }}
    >
      <div
        ref={dialog}
        role={dialogPresentation ? 'dialog' : 'region'}
        aria-modal={dialogPresentation ? 'true' : undefined}
        aria-label={settings.fork_session ? 'Fork session' : 'New session'}
        tabIndex={-1}
        className={
          dialogPresentation
            ? 'new-session-dialog card max-h-[90vh] w-full max-w-2xl overflow-y-auto'
            : `new-session-launcher w-full max-w-3xl${factory ? ' factory-session-launcher' : ''}`
        }
      >
        <div
          className={
            dialogPresentation
              ? 'flex items-center justify-between px-5 pt-4'
              : 'new-session-heading'
          }
        >
          <div>
            {dialogPresentation && (
              <div className="label">
                {settings.fork_session ? 'Fork session' : 'New session'}
              </div>
            )}
            <h1 className="text-base font-semibold">What should we work on?</h1>
          </div>
          {dialogPresentation && (
            <button
              className="btn btn-icon"
              aria-label="Close new session"
              disabled={busy}
              onClick={onClose}
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
        <div className={dialogPresentation ? 'px-5 pb-5 pt-4' : ''}>
          <div
            className="new-session-composer card relative"
            aria-busy={busy}
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
          >
            {composerChips}
            {attachmentErrorMessage}
            {promptField}
            <div className="new-session-actions border-t border-line px-2 py-2">
              <ActionMenu
                label="Options"
                disabled={busy}
                items={[
                  {
                    id: 'attach',
                    label: 'Attach files',
                    description: 'Included in the first message.',
                    icon: 'fileText',
                    onSelect: () => fileInput.current?.click(),
                  },
                  ...(support.supported ? [addRepository] : []),
                ]}
              />
              {fileField}
              <LaunchPills
                config={launchConfigCatalog}
                settings={settings}
                disabled={busy}
                providerLocked={!!settings.fork_session}
                onChange={updateSettings}
              />
              <span className="flex-1" />
              {submitButton}
            </div>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          )}
          {!selection.available && (
            <p role="status" className="mt-3 text-sm text-muted">
              {selection.provider?.execution_modes.some(
                (mode) => mode.available,
              )
                ? `${agentName} cannot run with the selected execution option here. Choose an available execution option or try again when the runtime is ready.`
                : selection.provider?.id === 'puck' &&
                    !selection.provider.models?.length
                  ? `No model is configured for ${agentName} in this environment. Ask an administrator to add one.`
                  : `No execution runtime is available for ${agentName} in this environment. Ask an administrator to enable one, or try again later.`}
            </p>
          )}
        </div>
        {repositoryDialogElement}
      </div>
    </div>
  )
}

async function initialMessageContent(
  prompt: string,
  attachments: UploadedInitialAttachment[],
): Promise<ContentBlock[]> {
  return Promise.all([
    ...(prompt ? [{ type: 'text' as const, text: prompt }] : []),
    ...attachments.map(
      async ({ attachment, data, upload }): Promise<ContentBlock> => {
        const file = upload.files[0]
        if (!file)
          throw new Error(`${attachment.name} was not saved. Retry the upload.`)
        if (attachment.file.type.startsWith('image/'))
          return {
            type: 'image',
            mimeType: attachment.file.type,
            data,
          }
        const uri = new URL(
          artifactFileUrl(
            upload.session_id,
            upload.id,
            file.id,
            false,
            'uploads',
          ),
          window.location.origin,
        ).href
        if (isTextFile(attachment.file))
          return {
            type: 'resource',
            resource: {
              uri,
              mimeType: 'text/plain',
              text: await attachment.file.text(),
            },
          }
        return {
          type: 'resource',
          resource: {
            uri,
            mimeType: attachment.file.type || 'application/octet-stream',
            blob: data,
          },
        }
      },
    ),
  ])
}

function isTextFile(file: File) {
  return (
    file.type.startsWith('text/') ||
    /\.(?:c|cc|cpp|css|csv|go|html?|java|js|json|jsx|md|mjs|py|rb|rs|sh|sql|svg|toml|ts|tsx|txt|xml|ya?ml)$/i.test(
      file.name,
    )
  )
}
